// Hlavní překreslení (render) a spuštění aplikace.
// (Krok B1b: rozděleno ze src/app.js beze změny kódu; importy/exporty doplněny automaticky.)
import { ICONS, beginRenderCache, canManageContent, canManageUsers, canWriteAudits, endRenderCache, esc, maybeProcessDeepLink, myRole, state } from './core/state.js';
import { maybePurgeOldDeletedAudits } from './core/pagination.js';
import { updateLicenceBanner } from './core/licence.js';
import { ensureCurrentCompanySelected, maybeMigrateAuditNumbers, maybeMigrateCompanies, renderNav, updateBrandingHeader, updateCompanySwitcherButton, updatePreviewBanner, updateTopbarSub } from './core/companies.js';
import { updateTopbarProfileButton } from './core/chrome.js';
import { renderDashboard, renderNavModeMenu, renderSideTasks, updateShellInsets } from './views/my-tasks.js';
import { renderBranding } from './views/places.js';
import { renderAudit } from './views/audit-runner.js';
import { renderHistory } from './views/table.js';
import { renderCorrective } from './views/corrective.js';
import { renderNoAccess } from './core/pdf.js';
import { renderSchedule, renderScheduleManage } from './views/schedule.js';
import { routerAfterRender, routerBeforeRender } from './core/router.js';
import { ensureAuditsScope } from './core/boot.js';
import { needsNameGate, renderNameGate } from './core/name-gate.js';

// ================= obrazovky načítané až při otevření =================
// Nastavení, Uživatelé a role, Druhy auditů a otázky a stránka pracoviště
// (QR) se stahují až při prvním otevření — rychlejší start aplikace.
// Jejich App.* funkce volané odjinud mají zástupce (lazyHandler), který
// modul nejdřív načte. Nová funkce volaná mimo modul = přidat lazyHandler.
var LAZY_VIEWS = {
  settings: function(){ return import('./views/settings.js'); },
  users: function(){ return import('./views/users.js'); },
  questions: function(){ return import('./views/questions.js'); },
  workplace: function(){ return import('./views/workplace.js'); },
  feedback: function(){ return import('./views/feedback.js'); }
};
var lazyMods = {}, lazyLoading = {};
function loadView(name){
  if(lazyMods[name]) return Promise.resolve(lazyMods[name]);
  if(!lazyLoading[name]){
    lazyLoading[name] = LAZY_VIEWS[name]().then(function(m){ lazyMods[name] = m; return m; },
      function(e){ delete lazyLoading[name]; throw e; });
  }
  return lazyLoading[name];
}
// Modul obrazovky, nebo null (pak se načte a stránka se překreslí).
function viewMod(name){
  if(lazyMods[name]) return lazyMods[name];
  loadView(name).then(function(){ try{ sessionStorage.removeItem('bozp-lazy-reload'); }catch(e){} render(); }, function(){
    // Po nasazení nové verze staré soubory na serveru nejsou → jednou obnovit stránku.
    try{ if(navigator.onLine && !sessionStorage.getItem('bozp-lazy-reload')){ sessionStorage.setItem('bozp-lazy-reload', '1'); location.reload(); return; } }catch(e){}
    var c = document.getElementById('content');
    if(c) c.innerHTML = '<div class="empty">Tuto část aplikace se nepodařilo načíst. Zkontrolujte připojení a zkuste to znovu.</div>';
  });
  var c = document.getElementById('content');
  if(c) c.innerHTML = '<div class="empty muted">Načítám…</div>';
  return null;
}
function lazyHandler(fnName, name){
  App[fnName] = function(){
    var args = arguments;
    return loadView(name).then(function(){ return App[fnName].apply(App, args); });
  };
}
// Pro testy: načíst všechny odložené obrazovky hned (tests/run.js).
window.__loadAllViews = function(){ return Promise.all(Object.keys(LAZY_VIEWS).map(loadView)); };
lazyHandler('openQrPrint', 'workplace');
lazyHandler('openQrScanner', 'workplace');
lazyHandler('toggleUserCompanyAccess', 'users');
// Volat jen když je modul načtený (viewMod), jinak se ukáže „Načítám…“.
function lazyRender(name, fn){ var m = viewMod(name); if(m) m[fn](); }

// ================= render dispatch =================
// Pole data a času se vykreslují s data-value a hodnota se doplní až sem (jako
// vlastnost .value, ne atribut value). Výchozí hodnota pole tak zůstane
// prázdná — tlačítko ve výběru data na iOS totiž "obnoví výchozí hodnotu",
// a kdyby jí bylo dřív vybrané datum, nešlo by ho smazat.
// JEDINÝ způsob, jak v appce vytvořit pole data nebo času — pravidla pro
// iOS jsou zabudovaná zde (viz CLAUDE.md, „Pole data a času“):
//  - hodnota jako data-value, ne value (doplní hydrateDateInputs),
//  - onChange se volá při input i change (iOS „Vymazat“ pošle jen input),
//  - handler NESMÍ volat render() — překreslení zavře otevřený výběr.
//    Pole, která mají překreslit seznam (filtry), onChange nemají a
//    připojí se po vykreslení přes wireDateFilter().
// opts: {type:'date'|'time', id, value, onChange:'App.fn(…,this.value)'}
function dateTimeInputHtml(opts){
  var type = opts.type==='time' ? 'time' : 'date';
  var h = opts.onChange ? ' oninput="'+opts.onChange+'" onchange="'+opts.onChange+'"' : ' data-filter="1"';
  return '<input type="'+type+'"'+(opts.id?' id="'+opts.id+'"':'')+' data-value="'+esc(opts.value||'')+'"'+h+'/>';
}
// Víceřádková pole rostou podle textu (žádné scrollování uvnitř pole).
// Bez překreslení — jen výška prvku. Volá se při psaní (globální
// posluchač níž) a po každém render() pro předvyplněný text.
function autosizeTextarea(el){
  if(!el || el.tagName!=='TEXTAREA') return;
  el.style.height = 'auto';
  el.style.height = (el.scrollHeight + (el.offsetHeight - el.clientHeight)) + 'px';
}
document.addEventListener('input', function(e){ autosizeTextarea(e.target); });
document.addEventListener('focusin', function(e){ autosizeTextarea(e.target); });
window.addEventListener('resize', function(){ document.querySelectorAll('#content textarea').forEach(autosizeTextarea); });
function hydrateDateInputs(root){
  if(!root) return;
  root.querySelectorAll('input[type=date][data-value], input[type=time][data-value]').forEach(function(el){
    if(el.value!==el.getAttribute('data-value')) el.value = el.getAttribute('data-value');
  });
}
function render(){
  var outer = beginRenderCache();
  try { renderNow(); } finally { endRenderCache(outer); }
  routerAfterRender();
}
function renderNow(){
  window.__renders = (window.__renders || 0) + 1; // pro testy: čekání na klid (tests/run.js)
  if(state.ready.companies){ ensureCurrentCompanySelected(); ensureAuditsScope(); }
  maybeMigrateCompanies();
  maybeMigrateAuditNumbers();
  if(state.ready.audits) maybePurgeOldDeletedAudits();
  maybeProcessDeepLink();
  routerBeforeRender();
  renderNav();
  updateTopbarSub();
  updatePreviewBanner();
  updateLicenceBanner();
  updateBrandingHeader();
  updateCompanySwitcherButton();
  updateTopbarProfileButton();
  renderNavModeMenu();
  var pageBrandEl = document.getElementById('pageBrand');
  if(pageBrandEl) pageBrandEl.classList.toggle('is-hidden', state.view==='dashboard');
  updateShellInsets();
  renderSideTasks();
  if(state.dbMissing){
    document.getElementById('content').innerHTML = '<div class="empty">'+ICONS.empty+
      '<p>Tato aplikace potřebuje přístup k datům. Otevřete ji prosím jako vlastník nebo s oprávněním k úpravám.</p></div>';
    return;
  }
  // Re-rendering replaces #content's innerHTML wholesale, which would normally
  // drop keyboard focus (and caret position) out of whatever text field the
  // person is actively typing into — e.g. a search box triggers a render on
  // every keystroke. Capture it beforehand and restore it onto the freshly
  // rendered element with the same id afterwards.
  var contentEl = document.getElementById('content');
  var active = document.activeElement;
  var focusInfo = null;
  if(active && active.id && contentEl && contentEl.contains(active) &&
     (active.tagName==='INPUT' || active.tagName==='TEXTAREA')){
    focusInfo = {id:active.id};
    try { focusInfo.selStart = active.selectionStart; focusInfo.selEnd = active.selectionEnd; } catch(e){}
  }
  if(needsNameGate()) renderNameGate(); else
  switch(state.view){
    case 'dashboard': renderDashboard(); break;
    case 'stats': renderDashboard(); break;
    case 'workplace': lazyRender('workplace', 'renderWorkplace'); break;
    case 'audit': canWriteAudits() ? renderAudit() : renderNoAccess(); break;
    case 'history': renderHistory(); break;
    case 'corrective': renderCorrective(); break;
    case 'schedule': renderSchedule(); break;
    case 'scheduleManage': canManageContent() ? renderScheduleManage() : renderNoAccess(); break;
    case 'settings': lazyRender('settings', 'renderSettings'); break;
    case 'profile': lazyRender('settings', 'renderProfile'); break;
    case 'appearance': lazyRender('settings', 'renderAppearance'); break;
    case 'dashboardSettings': lazyRender('settings', 'renderDashboardSettings'); break;
    case 'auditRules':
    case 'scoreSettings':
    case 'scheduleStartSettings': myRole()==='administrator' ? lazyRender('settings', 'renderAuditRules') : renderNoAccess(); break;
    case 'notificationSettings':
    case 'calendarSync': state.myId ? lazyRender('settings', 'renderNotificationSettings') : renderNoAccess(); break;
    case 'branding': canManageContent() ? renderBranding() : renderNoAccess(); break;
    case 'companies': myRole()==='administrator' ? renderBranding() : renderNoAccess(); break;
    case 'questions': canManageContent() ? lazyRender('questions', 'renderQuestions') : renderNoAccess(); break;
    case 'users': canManageUsers() ? lazyRender('users', 'renderUsersAdmin') : renderNoAccess(); break;
    case 'auditorGroups': canManageContent() ? lazyRender('users', 'renderAuditorGroups') : renderNoAccess(); break;
    case 'feedbackNew': state.myId ? lazyRender('feedback', 'renderFeedbackNew') : renderNoAccess(); break;
    case 'myFeedback': state.myId ? lazyRender('feedback', 'renderMyFeedback') : renderNoAccess(); break;
    case 'feedbackAdmin': myRole()==='administrator' ? lazyRender('feedback', 'renderFeedbackAdmin') : renderNoAccess(); break;
    case 'contactOperator': myRole()==='administrator' ? lazyRender('feedback', 'renderContactOperator') : renderNoAccess(); break;
    case 'companyAccess': if(canManageUsers()){ state.usersTab = 'access'; lazyRender('users', 'renderUsersAdmin'); } else renderNoAccess(); break;
    default: renderDashboard();
  }
  hydrateDateInputs(contentEl);
  if(contentEl) contentEl.querySelectorAll('textarea').forEach(autosizeTextarea);
  if(focusInfo){
    var el = document.getElementById(focusInfo.id);
    if(el && (el.tagName==='INPUT' || el.tagName==='TEXTAREA')){
      el.focus();
      if(typeof focusInfo.selStart==='number' && el.setSelectionRange){
        try { el.setSelectionRange(focusInfo.selStart, focusInfo.selEnd); } catch(e){}
      }
    }
  }
}

export {
  dateTimeInputHtml,
  loadView,
  render
};
